import React from "react";
import { Gift, Shield, UserCog } from "lucide-react";
import {
  formatCompact,
  ROLE_META,
  userInitials,
  walletSplit,
} from "./userFloorHelpers";
import { formatMemberSinceShort } from "./userDetailHelpers";
import UserDetailTabs from "./UserDetailTabs.jsx";

function WalletCard({ label, value, tone, flex }) {
  return (
    <div className={`au-wallet-card au-wallet-card-${tone}`}>
      <span className="au-wallet-card-label">{label}</span>
      <strong className="au-wallet-card-val">{value}</strong>
      <span className="au-wallet-card-bar" aria-hidden>
        <span style={{ flex: flex }} />
      </span>
    </div>
  );
}

export default function UserDetailPanel({
  user,
  detailUser,
  onEditRole,
  onReward,
  onBan,
}) {
  if (!user) return null;

  const role = user.role || "user";
  const roleMeta = ROLE_META[role] || { short: role, tone: "user" };
  const isBanned = Boolean(user.isBanned);
  const profile = detailUser?._id === user._id ? { ...user, ...detailUser } : user;
  const { cash, coin, cashFlex, coinFlex } = walletSplit(profile);
  const bannedAt = profile.bannedAt;
  const banReason = profile.banReason;
  const initials = userInitials(profile.name, profile.email);

  return (
    <section className="au-detail amf-detail" aria-label={`${user.name || "Account"} detail`}>
      <header className="au-profile-hero">
        <div className="au-profile-hero-main">
          <span
            className={`au-profile-avatar au-profile-avatar-${roleMeta.tone}${
              isBanned ? " au-profile-avatar-banned" : ""
            }`}
            aria-hidden
          >
            {initials}
          </span>
          <div className="au-profile-copy">
            <div className="au-profile-eyebrow">
              <span className={`au-profile-role au-user-role au-user-role-${roleMeta.tone}`}>
                {roleMeta.short}
              </span>
              {profile.userLevel ? (
                <span className="au-profile-level">{profile.userLevel}</span>
              ) : null}
              <span
                className={`au-profile-status${isBanned ? " au-profile-status-banned" : ""}`}
              >
                {isBanned ? "Banned" : "Active"}
              </span>
            </div>
            <h2 className="au-profile-name">{user.name || "Unknown"}</h2>
            <p className="au-profile-meta">
              {profile.email || "—"}
              {profile.inviteCode ? (
                <>
                  {" "}
                  · <em>#{profile.inviteCode}</em>
                </>
              ) : null}
            </p>
          </div>
        </div>

        <div className="au-profile-wallets">
          <WalletCard label="Cash" value={formatCompact(cash)} tone="cash" flex={cashFlex} />
          <WalletCard label="Coin" value={formatCompact(coin)} tone="coin" flex={coinFlex} />
        </div>

        <div className="au-profile-actions" role="toolbar" aria-label="Account actions">
          <button type="button" className="au-profile-act" onClick={onEditRole}>
            <UserCog size={14} aria-hidden />
            <span>Role</span>
          </button>
          <button type="button" className="au-profile-act au-profile-act-reward" onClick={onReward}>
            <Gift size={14} aria-hidden />
            <span>Reward</span>
          </button>
          <button
            type="button"
            className={`au-profile-act ${isBanned ? "au-profile-act-unban" : "au-profile-act-ban"}`}
            onClick={onBan}
          >
            <Shield size={14} aria-hidden />
            <span>{isBanned ? "Unban" : "Ban"}</span>
          </button>
        </div>
      </header>

      <div className="amf-detail-scroll au-detail-scroll">
        <div className="au-profile-facts">
          <div className="au-profile-fact">
            <span className="au-profile-fact-key">Joined</span>
            <span className="au-profile-fact-val">{formatMemberSinceShort(profile.createdAt)}</span>
          </div>
          <div className="au-profile-fact">
            <span className="au-profile-fact-key">Account role</span>
            <span className="au-profile-fact-val">{profile.role || "—"}</span>
          </div>
          {profile.role === "ambassador" ? (
            <>
              <div className="au-profile-fact au-profile-fact-highlight">
                <span className="au-profile-fact-key">Contact</span>
                <span className="au-profile-fact-val">
                  {profile.ambassadorContactPhone || profile.phoneNumber || "—"}
                </span>
              </div>
              <div className="au-profile-fact au-profile-fact-highlight">
                <span className="au-profile-fact-key">District</span>
                <span className="au-profile-fact-val">{profile.ambassadorLocation || "—"}</span>
              </div>
            </>
          ) : null}
        </div>

        {isBanned ? (
          <aside className="au-detail-ban" role="note">
            <strong>Suspended account</strong>
            <p>{banReason || "No reason recorded"}</p>
            {bannedAt ? (
              <span className="au-detail-ban-meta">Since {formatMemberSinceShort(bannedAt)}</span>
            ) : null}
          </aside>
        ) : null}

        <UserDetailTabs userId={user._id} />
      </div>
    </section>
  );
}
